import { z } from 'zod';

export const campaignQuestionSchema = z.object({
  id: z.string().min(1),
  kind: z.string(),
  difficulty: z.number().int().min(1).max(5).optional(),
  type: z.literal('choice'),
  optionTitleIds: z.tuple([z.string(), z.string(), z.string(), z.string()]),
  screenshots: z.array(z.string().min(1)).min(1),
});
export type CampaignQuestion = z.infer<typeof campaignQuestionSchema>;

/** Public title metadata can be reused; play state and rewards are never cached here. */
export const createCampaignQuestionLoader = (
  getQuestion: (id: string) => Promise<unknown>,
  getTitles: (ids: string[]) => Promise<{ data: { id: string; name: string }[] }>,
) => {
  const names = new Map<string, string>();
  const pendingNames = new Map<string, Promise<void>>();
  return async (id: string, kind: string, source?: unknown) => {
    const provided = campaignQuestionSchema.safeParse(source);
    const question = provided.success ? provided.data : campaignQuestionSchema.parse(await getQuestion(id));
    if (question.id !== id || question.kind !== kind) throw new Error('Question does not match chapter');
    const ids = [...new Set(question.optionTitleIds)];
    const missing = ids.filter((key) => !names.has(key) && !pendingNames.has(key));
    if (missing.length) {
      const request = getTitles(missing).then((response) => {
        for (const title of response.data) if (missing.includes(title.id)) names.set(title.id, title.name);
        if (missing.some((key) => !names.has(key))) throw new Error('Missing option titles');
      });
      for (const key of missing) pendingNames.set(key, request);
    }
    const waiting = ids.filter((key) => pendingNames.has(key));
    const requests = [...new Set(waiting.map((key) => pendingNames.get(key)!))];
    try {
      await Promise.all(requests);
    } finally {
      for (const key of waiting) {
        if (requests.includes(pendingNames.get(key)!)) pendingNames.delete(key);
      }
    }
    return {
      question,
      options: question.optionTitleIds.map((key) => names.get(key)!) as [string, string, string, string],
    };
  };
};
